import {
  Box,
  Button,
  CardMedia,
  Checkbox,
  Input,
  ListItemText,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import React, { ChangeEvent, useState, useEffect } from "react";
import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useUpdateProfileMutation,
  useSendOtpToVerifyMutation,
  useVerifyOtpToVerifyMutation,
  useLazyMyProfileQuery,
} from "../../service/auth";
import { OTP_TYPE } from "../../utils/enums";
import { showError, showToast } from "../../constants/toasts";
import { Modal } from "@mui/material";
import OTPInput from "react-otp-input";
import VerifiedIcon from "@mui/icons-material/Verified";
import PhoneInput from "react-phone-input-2";
import { isValidPhoneNumber } from "libphonenumber-js";
import Loader from "../../constants/Loader";
import { UploadMedia, Uploadpdf } from "../../utils/mediaUpload";
import { useLazyGetCategoryQuery } from "../../service/main";
import { Category } from "../../types/General";
import { useImageVerification } from "../../hooks/imageVerification";
import { validateUAEIBAN } from "../../utils/validations";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

const SignupForm = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    let interval: any;
    if (timer > 0 && openModal) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, openModal]);
  const [verificationType, setVerificationType] = useState<
    "email" | "phone" | null
  >(null);
  const [phoneLength, setPhoneLength] = useState(12);
  const [countryCode, setCountryCode] = useState("91");
  const countryCodeRef = React.useRef("91");

  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpToVerifyMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] =
    useVerifyOtpToVerifyMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] =
    useUpdateProfileMutation();
  const [myProfile] = useLazyMyProfileQuery({});
  const [getCategory] = useLazyGetCategoryQuery();
  const [user, setUser] = useState<any>(null);
  const { validateImage, loading: loadingImage } = useImageVerification();

  useEffect(() => {
    if (user?.user?.phone) {
      let cc = "";
      if (user?.user?.countryCode) {
        cc = user.user.countryCode.replace("+", "");
        setCountryCode(cc);
        countryCodeRef.current = cc;
      } else {
        const ph = user.user.phone;
        if (ph.startsWith("971")) cc = "971";
        else if (ph.startsWith("91")) cc = "91";
        else if (ph.startsWith("86")) cc = "86";
        setCountryCode(cc);
        countryCodeRef.current = cc;
      }
      setPhoneLength(cc.length + user.user.phone.length);
    }
  }, [user]);

  const initialPhoneValue = React.useMemo(() => {
    if (!user) return null;
    const code = (user?.user?.countryCode || "").replace(/\D/g, "");
    let phoneStr = (user?.user?.phone || "").replace(/\D/g, "");
    if (code && phoneStr.startsWith(code) && phoneStr.length > code.length + 5) {
      phoneStr = phoneStr.slice(code.length);
    }
    return code ? code + phoneStr : phoneStr;
  }, [user]);


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const step1Schema = Yup.object().shape({
    fullName: Yup.string().required("Full Name is required"),
    businessName: Yup.string().required("Business Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .required("Phone number is required")
      .test("is-valid-phone", "Invalid phone number for the selected country", (val, context) => {
        if (!val) return context.createError({ message: "Phone number is required" });
        const cleanVal = val.replace(/\D/g, "");
        if (cleanVal === countryCodeRef.current || cleanVal === "") {
          return context.createError({ message: "Please enter a valid phone number for selected country" });
        }
        try {
          if (!isValidPhoneNumber("+" + cleanVal)) {
            return context.createError({ message: "Invalid phone number for the selected country" });
          }
          return true;
        } catch (error) {
          return context.createError({ message: "Invalid phone number for the selected country" });
        }
      }),
    coverageArea: Yup.string().required("Coverage Area is required"),
    description: Yup.string().max(250, "Max 250 characters"),
  });

  const step2Schema = Yup.object().shape({
    addressDetails: Yup.string().required("Address details are required"),
    emirate: Yup.string().required("Emirate is required"),
    city: Yup.string().required("City is required"),
    streetName: Yup.string().required("Street name is required"),
    buildingName: Yup.string().required("Building name is required"),
  });

  const step3Schema = Yup.object().shape({
    bankAccountName: Yup.string()
      .matches(
        /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/,
        "Bank Account Name cannot contain leading, trailing, or multiple spaces, and special characters are not allowed"
      )
      .required("Bank Account Name is required"),
    bankName: Yup.string()
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        "Special characters are not allowed in Bank Name"
      )
      .required("Bank Name is required"),
    iban: Yup.string()
      .trim()
      .required("IBAN is required")
      .test("is-valid-uae-iban", function (value) {
        if (!value) return false;
        const result = validateUAEIBAN(value, false);
        if (!result.valid) {
          return this.createError({ message: result.error });
        }
        return true;
      }),
    swiftCode: Yup.string()
      .matches(
        /^[a-zA-Z0-9]*$/,
        "Special characters are not allowed in Swift Code"
      )
      .notRequired(),
    bankDocument: Yup.string().notRequired(),
  });

  const step4Schema = Yup.object().shape({
    document1: Yup.string().required("Document 1 is required"),
    document2: Yup.string().notRequired(),
  });

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      formik.setFieldValue("logoImage", "");
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      showError("File size exceeds the allowed limit");
      return;
    }

    if (!file.type.startsWith("image/")) {
      showError("Unsupported image format.");
      return;
    }

    const result = await validateImage(file);
    if (!result.valid) {
      showError(result.error);
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      const url =
        imageUrl.data?.imageUrl ||
        imageUrl.data?.image ||
        imageUrl.data?.url ||
        (typeof imageUrl.data === "string" ? imageUrl.data : "");
      formik.setFieldValue("logoImage", url || "");
      showToast("Image uploaded successfully.");
    } else {
      showError("Upload failed due to a network/server error");
    }
  };

  const handleBankDocumentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      formik.setFieldValue("bankDocument", "");
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      showError("File size exceeds the allowed limit");
      return;
    }

    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      showError("Unsupported file format");
      return;
    }

    const imageUrl = (await Uploadpdf(file)) as any;
    if (imageUrl?.data) {
      const url =
        imageUrl.data?.imageUrl ||
        imageUrl.data?.image ||
        imageUrl.data?.url ||
        (typeof imageUrl.data === "string" ? imageUrl.data : "");
      formik.setFieldValue("bankDocument", url || "");
      showToast("Document uploaded successfully.");
    } else {
      showError("Upload failed due to a network/server error");
    }
  };

  const handleAdditionalDocumentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: "document1" | "document2"
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      formik.setFieldValue(fieldName, "");
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      showError("File size exceeds the allowed limit");
      return;
    }

    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      showError("Unsupported file format");
      return;
    }

    const imageUrl = (await Uploadpdf(file)) as any;
    if (imageUrl?.data) {
      const url =
        imageUrl.data?.imageUrl ||
        imageUrl.data?.image ||
        imageUrl.data?.url ||
        (typeof imageUrl.data === "string" ? imageUrl.data : "");
      formik.setFieldValue(fieldName, url || "");
      showToast("Document uploaded successfully.");
    } else {
      showError("Upload failed due to a network/server error");
    }
  };

  const formik = useFormik({
    initialValues: {
      fullName: user?.sp?.name || user?.user?.fullName || "",
      businessName: user?.sp?.businessName || "",
      email: user?.user?.email || "",
      phone: (() => {
        const code = (user?.user?.countryCode || "").replace(/\D/g, "");
        let phoneStr = (user?.user?.phone || "").replace(/\D/g, "");
        if (code && phoneStr.startsWith(code) && phoneStr.length > code.length + 5) {
          phoneStr = phoneStr.slice(code.length);
        }
        return code ? code + phoneStr : phoneStr;
      })(),
      coverageArea: user?.sp?.serviceArea?.[0] || "default",
      description: user?.sp?.description || "",
      logoImage: user?.sp?.logo || "",
      addressDetails: user?.sp?.address?.detail || "",
      emirate: user?.sp?.address?.emirate || "",
      city: user?.sp?.address?.city || "",
      streetName: user?.sp?.address?.streetName || "",
      buildingName: user?.sp?.address?.buildingName || "",
      bankAccountName: user?.sp?.bankDetails?.accountName || "",
      bankName: user?.sp?.bankDetails?.bankName || "",
      iban: user?.sp?.bankDetails?.iban || "",
      swiftCode: user?.sp?.bankDetails?.swift || "",
      bankDocument: user?.sp?.bankDetails?.document || "",
      document1: user?.sp?.additionalDocuments?.[0] || "",
      document2: user?.sp?.additionalDocuments?.[1] || "",
    },
    enableReinitialize: true,
    validationSchema:
      activeStep === 0
        ? step1Schema
        : activeStep === 1
          ? step2Schema
          : activeStep === 2
            ? step3Schema
            : step4Schema,
    onSubmit: async (values) => {
      try {
        let payload: any = {
          step: activeStep + 1,
        };

        if (activeStep === 0) {
          payload = {
            ...payload,
            name: values.fullName,
            businessName: values.businessName,
            email: values.email,
            phone: values.phone.startsWith(countryCode) ? values.phone.slice(countryCode.length) : values.phone,
            countryCode: "+" + countryCode,
            description: values.description,
            category: selectedCategories,
            serviceArea: [values.coverageArea],
            ...(values.logoImage && { logo: values.logoImage }),
          };
        } else if (activeStep === 1) {
          payload = {
            ...payload,
            address: {
              detail: values.addressDetails,
              emirate: values.emirate,
              city: values.city,
              streetName: values.streetName,
              buildingName: values.buildingName,
            },
          };
        } else if (activeStep === 2) {
          const checksumCheck = validateUAEIBAN(values.iban, true);
          if (!checksumCheck.valid && checksumCheck.error === "Please enter a valid IBAN.") {
            formik.setFieldError("iban", checksumCheck.error);
            showError(checksumCheck.error);
            return;
          }
          payload = {
            ...payload,
            bankDetails: {
              accountName: values.bankAccountName,
              bankName: values.bankName,
              iban: values.iban.replace(/\s+/g, '').toUpperCase(),
              swift: values.swiftCode,
              document: values.bankDocument,
            },
          };
        } else if (activeStep === 3) {
          payload = {
            ...payload,
            step: 4,
            isProfileComplete: true,
            additionalDocuments: [values.document1, values.document2].filter(
              Boolean
            ),
          };
        }

        const response = await updateProfile(payload).unwrap();
        if (response.statusCode === 200 || response.statusCode === 201) {
          myProfileApi();
          if (activeStep === 3) {
            showToast("Profile completed successfully");
            navigate("/dashboard");
          } else {
            showToast(
              activeStep === 0
                ? "General details saved successfully"
                : activeStep === 1
                  ? "Address details saved successfully"
                  : "Bank details saved successfully"
            );
            handleNext();
          }
        }
      } catch (error: any) {
        showError(error?.data?.message || "Failed to update profile");
      }
    },
  });

  const [phoneKey, setPhoneKey] = useState("default");

  useEffect(() => {
    if (user && formik.values.phone === initialPhoneValue) {
      setPhoneKey(initialPhoneValue || "synced");
    }
  }, [user, formik.values.phone, initialPhoneValue]);


  const handleSendVerificationOtp = async (target: "email" | "phone") => {
    try {
      setVerificationType(target);
      const payload: any = {
        otpType: OTP_TYPE.UPDATE,
      };
      if (target === "email") {
        payload.email = formik.values.email;
      } else {
        const cleanPhone = formik.values.phone.replace("+", "");
        
        // Validate before sending OTP
        const validateVal = "+" + cleanPhone;
        let isValid = false;
        try {
          isValid = isValidPhoneNumber(validateVal);
        } catch (error) {
          isValid = false;
        }

        if (!isValid) {
          showError(`Invalid phone number for the selected country`);
          formik.setFieldTouched("phone", true, true);
          return;
        }

        payload.phone = cleanPhone.slice(countryCode.length);
        payload.countryCode = "+" + countryCode;
      }

      const response = await sendOtp(payload).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        setTimer(60);
        showToast("OTP sent successfully");




        setOpenModal(true);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to send OTP");
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 4) {
      showError("Please enter 4-digit OTP");
      return;
    }
    try {
      const payload: any = {
        otp: otp,
        otpType: OTP_TYPE.UPDATE,
      };
      if (verificationType === "email") {
        payload.email = formik.values.email;
      } else {
        payload.phone = formik.values.phone.slice(countryCode.length);
        payload.countryCode = "+" + countryCode;
      }

      const response = await verifyOtp(payload).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        showToast("Verified successfully");
        setOpenModal(false);
        setOtp("");
        myProfileApi();
      } else {
        showError(response.message || "Verification failed");
        setOtp("");
      }
    } catch (error: any) {
      setOtp("");
      showError(error?.data?.message || "Verification failed");
    }
  };

  const steps = [
    "General Details",
    "Address Details",
    "Bank Details",
    "Additional Documents",
  ];

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <form className="form" onSubmit={formik.handleSubmit}>
              {/* IMAGE UPLOAD */}
              <div className="gap_p">
                <div className="control_group w_100">
                  <label htmlFor="icon-button-file">Upload Logo</label>
                  {formik.values.logoImage ? (
                    <div className="upload_image">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia
                            component="img"
                            image={formik.values.logoImage}
                            alt="photo"
                          />
                        </figure>
                        <CloseIcon
                          onClick={() => {
                            formik.setFieldValue("logoImage", "");
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <label className="upload_image" htmlFor="icon-button-file">
                      <Input
                        sx={{ display: "none" }}
                        id="icon-button-file"
                        type="file"
                        inputProps={{
                          accept: "image/png,image/jpeg",
                        }}
                        onChange={(e: any) => {
                          handleProfileUpload(e);
                          e.target.value = null;
                        }}
                      />
                      <span className="upload_image_holder">
                        <figure className="add_img">
                          <img
                            src="/static/images/user_placeholder.png"
                            alt="icon"
                          />
                          <span className="upload_img_icon">
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="icon"
                            />
                          </span>
                        </figure>
                      </span>
                    </label>
                  )}
                </div>

                {/* FULL NAME */}
                <div className="control_group w_33">
                  <label htmlFor="fullName">Full Name</label>
                  <TextField
                    id="fullName"
                    name="fullName"
                    className="text_field"
                    placeholder="Enter Full Name"
                    fullWidth
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.fullName && Boolean(formik.errors.fullName)
                    }
                    helperText={
                      formik.touched.fullName &&
                      (formik.errors.fullName as string)
                    }
                  />
                </div>

                {/* BUSINESS NAME */}
                <div className="control_group w_33">
                  <label htmlFor="businessName">Business Name</label>
                  <TextField
                    id="businessName"
                    name="businessName"
                    className="text_field"
                    placeholder="Enter Business Name"
                    fullWidth
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.businessName &&
                      Boolean(formik.errors.businessName)
                    }
                    helperText={
                      formik.touched.businessName &&
                      (formik.errors.businessName as string)
                    }
                  />
                </div>

                {/* CONTACT EMAIL */}
                <div className="control_group w_33">
                  <label htmlFor="email">Contact Email</label>
                  <div className="verify_input">
                    <TextField
                      id="email"
                      name="email"
                      type="email"
                      className="text_field"
                      placeholder="Enter Email Address"
                      fullWidth
                      disabled={user?.user?.isEmailVerified}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={
                        formik.touched.email && (formik.errors.email as string)
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {user?.user?.isEmailVerified ? (
                              <VerifiedIcon color="success" />
                            ) : (
                              <p
                                onClick={() =>
                                  handleSendVerificationOtp("email")
                                }
                                style={{
                                  cursor: "pointer",
                                  color: "#D3B88C",
                                  marginRight: "15px",
                                  fontSize: "14px",
                                }}
                              >
                                Verify
                              </p>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </div>

                {/* CONTACT PHONE */}
                <div
                  className="control_group w_33"
                  style={{ position: "relative" }}
                >
                  <label htmlFor="phone">Contact Phone</label>
                  <div className="verify_input">
                    <PhoneInput
                      key={phoneKey}
                      inputProps={{
                        id: "phone",

                        name: "phone",
                        placeholder: "Enter Phone Number",
                      }}
                      country={"in"}
                      autoFormat={false}
                      value={formik.values.phone}
                      onChange={(value, data: any) => {
                        formik.setFieldValue("phone", value);
                        if (data?.dialCode) {
                          setCountryCode(data.dialCode);
                          countryCodeRef.current = data.dialCode;
                        }
                        const dots = (data.format.match(/\./g) || []).length;
                        if (dots > 0) {
                          setPhoneLength(dots);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched("phone", true)}
                      disabled={user?.user?.isPhoneVerified}
                    />
                    <div className="eye_btn">
                      {user?.user?.isPhoneVerified ? (
                        <VerifiedIcon color="success" />
                      ) : formik.values.phone?.length < 5 ? null : (
                        <p
                          onClick={() => {
                            handleSendVerificationOtp("phone");
                          }}
                          style={{
                            cursor: "pointer",
                            color: "#D3B88C",
                            marginRight: "6px",
                            marginTop: "30px",
                          }}
                        >
                          Verify
                        </p>
                      )}
                    </div>
                    {formik.touched.phone && formik.errors.phone ? (
                      <p className="err_txt">{formik.errors.phone as string}</p>
                    ) : null}
                  </div>
                </div>

                {/* SERVICE COVERAGE AREA (LOCATIONS) */}
                <div className="control_group w_33">
                  <label htmlFor="coverageArea">Service Coverage Area (Locations)</label>
                  <Select
                    name="coverageArea"
                    fullWidth
                    value={formik.values.coverageArea}
                    onChange={formik.handleChange}
                    inputProps={{ id: "coverageArea" }}
                  >
                    <MenuItem value="default">Select Coverage Area</MenuItem>
                    <MenuItem value="Abu Dhabi">Abu Dhabi</MenuItem>
                    <MenuItem value="Dubai">Dubai</MenuItem>
                    <MenuItem value="Sharjah">Sharjah</MenuItem>
                    <MenuItem value="Ajman">Ajman</MenuItem>
                    <MenuItem value="Umm al-Quwain">Umm al-Quwain</MenuItem>
                    <MenuItem value="Ras al-Khaimah">Ras al-Khaimah</MenuItem>
                    <MenuItem value="Fujairah">Fujairah</MenuItem>
                  </Select>
                </div>

                {/* CATEGORY MULTI SELECT */}
                <div className="control_group w_33">
                  <label htmlFor="category">Category </label>

                  <Select
                    multiple
                    displayEmpty
                    open={openCategory}
                    onOpen={() => setOpenCategory(true)}
                    onClose={() => setOpenCategory(false)}
                    value={selectedCategories}
                    renderValue={(selected) => {
                      if ((selected as string[]).length === 0) {
                        return (
                          <span style={{ color: "#999" }}>Select Category</span>
                        );
                      }
                      return (selected as string[])
                        .map(
                          (id) => categories.find((cat) => cat.id === id)?.name
                        )
                        .filter(Boolean)
                        .join(", ");
                    }}
                    fullWidth
                    MenuProps={{
                      PaperProps: { style: { maxHeight: 300 } },
                    }}
                  >
                    {/* Select All / Clear All */}
                    <MenuItem
                      value="all"
                      onClick={(e) =>
                        handleSelectAll(
                          e,
                          categories?.map((cat) => cat?.id),
                          selectedCategories,
                          setSelectedCategories
                        )
                      }
                    >
                      <Checkbox
                        checked={
                          categories.length > 0 &&
                          selectedCategories.length === categories.length
                        }
                        indeterminate={
                          selectedCategories.length > 0 &&
                          selectedCategories.length < categories.length
                        }
                      />
                      <ListItemText
                        primary={
                          selectedCategories.length === categories.length
                            ? "Clear All"
                            : "Select All"
                        }
                      />
                    </MenuItem>

                    {categories?.map((cat) => (
                      <MenuItem
                        key={cat?.id}
                        value={cat?.id}
                        onClick={(e) =>
                          handleItemClick(
                            e,
                            cat?.id,
                            selectedCategories,
                            setSelectedCategories
                          )
                        }
                      >
                        <Checkbox
                          checked={selectedCategories.includes(cat?.id)}
                        />
                        <ListItemText primary={cat?.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  {error && !selectedCategories?.length ? (
                    <h6 className="err_msg">Category is required</h6>
                  ) : (
                    ""
                  )}
                </div>

                {/* BUSINESS DESCRIPTION */}
                <div className="control_group w_100 ai_rec_btn">
                  <label htmlFor="description">
                    Business Description
                    <p style={{ margin: 0 }}>(250 characters maximum)</p>
                  </label>
                  <TextField
                    id="description"
                    name="description"
                    placeholder="Enter Business Description"
                    multiline
                    fullWidth
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.description &&
                      Boolean(formik.errors.description)
                    }
                    helperText={
                      formik.touched.description &&
                      (formik.errors.description as string)
                    }
                  />

                </div>
              </div>
            </form>
          </>
        );
      case 1:
        return (
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              {/* ADDRESS DETAILS */}
              <div className="control_group w_50">
                <label htmlFor="addressDetails">Address Details</label>
                <TextField
                  id="addressDetails"
                  name="addressDetails"
                  className="text_field"
                  placeholder="Enter Full Address Details"
                  fullWidth
                  value={formik.values.addressDetails}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.addressDetails &&
                    Boolean(formik.errors.addressDetails)
                  }
                  helperText={
                    formik.touched.addressDetails &&
                    (formik.errors.addressDetails as string)
                  }
                />
              </div>

              {/* EMIRATE */}
              <div className="control_group w_50">
                <label htmlFor="emirate">Emirate</label>
                <Select
                  id="emirate"
                  name="emirate"
                  fullWidth
                  value={formik.values.emirate || "default"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                >
                  <MenuItem disabled value="default">
                    Select Emirate
                  </MenuItem>
                  <MenuItem value="Abu Dhabi">Abu Dhabi</MenuItem>
                  <MenuItem value="Dubai">Dubai</MenuItem>
                  <MenuItem value="Sharjah">Sharjah</MenuItem>
                  <MenuItem value="Ajman">Ajman</MenuItem>
                  <MenuItem value="Umm Al Quwain">Umm Al Quwain</MenuItem>
                  <MenuItem value="Ras Al Khaimah">Ras Al Khaimah</MenuItem>
                  <MenuItem value="Fujairah">Fujairah</MenuItem>
                </Select>
                {formik.touched.emirate && formik.errors.emirate && (
                  <p className="err_txt">{formik.errors.emirate as string}</p>
                )}
              </div>

              {/* CITY */}
              <div className="control_group w_50">
                <label htmlFor="city">City</label>
                <TextField
                  id="city"
                  name="city"
                  className="text_field"
                  placeholder="Enter City"
                  fullWidth
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={
                    formik.touched.city && (formik.errors.city as string)
                  }
                />
              </div>

              {/* STREET NAME */}
              <div className="control_group w_50">
                <label htmlFor="streetName">Street Name</label>
                <TextField
                  id="streetName"
                  name="streetName"
                  className="text_field"
                  placeholder="Enter Street Name"
                  fullWidth
                  value={formik.values.streetName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.streetName &&
                    Boolean(formik.errors.streetName)
                  }
                  helperText={
                    formik.touched.streetName &&
                    (formik.errors.streetName as string)
                  }
                />
              </div>

              {/* BUILDING NAME/NUMBER */}
              <div className="control_group w_50">
                <label htmlFor="buildingName">Building Name/Number</label>
                <TextField
                  id="buildingName"
                  name="buildingName"
                  className="text_field"
                  placeholder="Enter Building Name or Number"
                  fullWidth
                  value={formik.values.buildingName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.buildingName &&
                    Boolean(formik.errors.buildingName)
                  }
                  helperText={
                    formik.touched.buildingName &&
                    (formik.errors.buildingName as string)
                  }
                />
              </div>
            </div>
          </form>
        );
      case 2:
        return (
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="gap_p bank_details">
              {/* BANK ACCOUNT NAME */}
              <div className="control_group w_50">
                <label htmlFor="bankAccountName">
                  Bank Account Name (Mandatory)
                </label>
                <TextField
                  id="bankAccountName"
                  name="bankAccountName"
                  placeholder="Enter Bank Account Name"
                  className="text_field"
                  fullWidth
                  value={formik.values.bankAccountName}
                  onChange={(e) => {
                    const value = e.target.value.trimStart().replace(/[^a-zA-Z0-9 ]/g, "");
                    formik.setFieldValue("bankAccountName", value);
                  }}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.bankAccountName &&
                    Boolean(formik.errors.bankAccountName)
                  }
                  helperText={
                    formik.touched.bankAccountName &&
                    (formik.errors.bankAccountName as string)
                  }
                />
              </div>

              {/* BANK NAME */}
              <div className="control_group w_50">
                <label htmlFor="bankName">Bank Name (Mandatory)</label>
                <TextField
                  id="bankName"
                  name="bankName"
                  placeholder="Enter Bank Name"
                  className="text_field"
                  fullWidth
                  value={formik.values.bankName}
                  onChange={(e) => {
                    const value = e.target.value.trimStart().replace(/[^a-zA-Z0-9 ]/g, "");
                    formik.setFieldValue("bankName", value);
                  }}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.bankName && Boolean(formik.errors.bankName)
                  }
                  helperText={
                    formik.touched.bankName &&
                    (formik.errors.bankName as string)
                  }
                />
              </div>

              {/* IBAN */}
              <div className="control_group w_50">
                <label htmlFor="iban">IBAN (Mandatory)</label>
                <TextField
                  id="iban"
                  name="iban"
                  placeholder="Enter IBAN Number"
                  className="text_field"
                  fullWidth
                  value={formik.values.iban}
                  onChange={(e) => {
                    const value = e.target.value.trimStart().replace(/[^a-zA-Z0-9 ]/g, "");
                    formik.setFieldValue("iban", value);
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    const val = e.target.value;
                    const res = validateUAEIBAN(val, true);
                    if (!res.valid && res.error === "Please enter a valid IBAN.") {
                      setTimeout(() => formik.setFieldError("iban", res.error), 0);
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={formik.touched.iban && Boolean(formik.errors.iban)}
                  helperText={
                    formik.touched.iban && (formik.errors.iban as string)
                  }
                />
              </div>

              {/* SWIFT / BIC CODE */}
              <div className="control_group w_50">
                <label htmlFor="swiftCode">Swift Code (Optional)</label>
                <TextField
                  id="swiftCode"
                  name="swiftCode"
                  placeholder="Enter Swift/BIC Code"
                  fullWidth
                  value={formik.values.swiftCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                    formik.setFieldValue("swiftCode", value);
                  }}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* BANK DOCUMENT UPLOAD */}
              <div className="control_group w_100">
                <label htmlFor="bankDocument">
                  Bank Document Upload (Optional)
                </label>
                <div className="upload_box">
                  {formik.values.bankDocument ? (
                    <div
                      className="uploaded_file"
                      style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "150px",
                        border: "1px dashed #D3B88C",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        className="file_info"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                        }}
                      >
                        {formik.values.bankDocument
                          .toLowerCase()
                          .match(/\.(pdf|doc|docx)$/) ? (
                          <>
                            <PictureAsPdfIcon
                              sx={{ color: "#D3B88C", fontSize: "30px" }}
                            />
                            <span>Document Uploaded</span>
                          </>
                        ) : (
                          <img
                            src={formik.values.bankDocument}
                            alt="preview"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "150px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </div>
                      <IconButton
                        size="small"
                        onClick={() => formik.setFieldValue("bankDocument", "")}
                        sx={{ position: "absolute", top: 5, right: 5 }}
                      >
                        <CloseIcon color="error" />
                      </IconButton>
                    </div>
                  ) : (
                    <label className="upload_action">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e: any) => {
                          handleBankDocumentUpload(e);
                          e.target.value = null;
                        }}
                        style={{ display: "none" }}
                      />
                      <p>
                        <img src="/static/images/upload_icon.svg" alt="icon" />
                        <strong>Click to upload or drag and drop</strong>
                      </p>
                    </label>
                  )}
                </div>
              </div>
            </div>
          </form>
        );
      case 3:
        return (
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="gap_p bank_details">
              {[
                { label: "Document 1", name: "document1" as const },
                { label: "Document 2", name: "document2" as const },
              ].map((doc, idx) => (
                <div key={idx} className="control_group w_50">
                  <label htmlFor={doc.name}>
                    {doc.label}{" "}
                    {doc.name === "document1" ? "(Mandatory)" : "(Optional)"}
                  </label>
                  <div className="upload_box">
                    {formik.values[doc.name] ? (
                      <div
                        className="uploaded_file"
                        style={{
                          position: "relative",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "135px",
                          border: "1px dashed #D3B88C",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          className="file_info"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                          }}
                        >
                          {formik.values[doc.name]
                            .toLowerCase()
                            .match(/\.(pdf|doc|docx)$/) ? (
                            <>
                              <PictureAsPdfIcon
                                sx={{ color: "#D3B88C", fontSize: "30px" }}
                              />
                              <span>Document Uploaded</span>
                            </>
                          ) : (
                            <img
                              src={formik.values[doc.name]}
                              alt="preview"
                              style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: "135px",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                          )}
                        </div>
                        <IconButton
                          size="small"
                          onClick={() => formik.setFieldValue(doc.name, "")}
                          sx={{ position: "absolute", top: 5, right: 5 }}
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      </div>
                    ) : (
                      <label className="upload_action">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          style={{ display: "none" }}
                          onChange={(e: any) => {
                            handleAdditionalDocumentUpload(e, doc.name);
                            e.target.value = null;
                          }}
                        />
                        <p>
                          <img
                            src="/static/images/upload_icon.svg"
                            alt="icon"
                          />
                          <strong>Click to upload or drag and drop</strong>
                        </p>
                      </label>
                    )}
                  </div>
                  {formik.touched[doc.name] && formik.errors[doc.name] && (
                    <p className="err_txt">
                      {formik.errors[doc.name] as string}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </form>
        );

      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setIsOtpVerified(false);
  };

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [openCategory, setOpenCategory] = useState(false);

  const handleItemClick = (
    event: React.MouseEvent,
    value: string,
    selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    event.stopPropagation();
    const selectedIndex = selectedItems.indexOf(value);
    let newSelected: string[] = [];

    if (selectedIndex === -1) newSelected = [...selectedItems, value];
    else newSelected = selectedItems.filter((v) => v !== value);

    setSelectedItems(newSelected);
  };

  // ✅ Select All / Clear All handler
  const handleSelectAll = (
    event: React.MouseEvent,
    allItems: string[],
    selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    event.stopPropagation();
    if (selectedItems.length === allItems.length) setSelectedItems([]);
    else setSelectedItems(allItems);
  };

  const myProfileApi = async () => {
    setLoading(true);
    try {
      const response = await myProfile({}).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setSelectedCategories(
          response?.data?.sp?.categories?.map((item: any) =>
            String(item.categoryId)
          ) || []
        );
        setUser(response?.data || null);
      } else {
        setUser(null);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getCategoryApi = async () => {
    setLoading(true);
    try {
      const response = await getCategory({}).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setCategories(
          response?.data?.data?.filter(
            (cat: any) => !cat.isBlocked && !cat.isDeleted
          ) || []
        );
      } else {
        setCategories([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    myProfileApi();
    getCategoryApi();
  }, []);

  return (
    <>
      <Loader isLoad={loading} />
      <AuthLayout>
        <div className="authBox signup_box">
          <section className="wrap_viewDemo_steps uh_spc">
            <div className="container">
              <div className="page_head hd_3">
                <h2>{steps[activeStep]}</h2>
              </div>

              <div className="steps_grid">
                <div className="step_lt">
                  <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => {
                      const percentage = Math.round(
                        ((index + 1) / steps.length) * 100
                      );
                      return (
                        <Step key={index}>
                          <StepLabel>
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {percentage}%
                              </Typography>
                            </Box>
                          </StepLabel>
                        </Step>
                      );
                    })}
                  </Stepper>
                </div>

                <div className="step_rt">
                  <div className="shadow_box shadow_box1">
                    {activeStep === steps.length ? (
                      <div className="completed_step">
                        <Typography>
                          All steps completed - you're finished!
                        </Typography>
                        <Button onClick={handleReset} sx={{ mt: 2 }}>
                          Reset
                        </Button>
                      </div>
                    ) : (
                      <>
                        {renderStepContent(activeStep)}

                        <div className="btn_flex">
                          <Button
                            className="btn btn_grey"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                          >
                            Back
                          </Button>

                          {activeStep === steps.length - 1 ? (
                            <Button
                              className="btn btn_primary"
                              onClick={() => formik.handleSubmit()}
                              disabled={isUpdatingProfile}
                            >
                              {isUpdatingProfile
                                ? "Submitting..."
                                : "Complete Registration"}
                            </Button>
                          ) : (
                            <Button
                              className="btn btn_primary"
                              onClick={() => formik.handleSubmit()}
                              disabled={isUpdatingProfile}
                            >
                              {isUpdatingProfile ? "Saving..." : "Next"}
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Modal
            open={openModal}
            onClose={() => setOpenModal(false)}
            aria-labelledby="otp-modal-title"
          >
            <Box className="authBox" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 480, maxWidth: '95%', outline: 'none' }}>
              <IconButton onClick={() => setOpenModal(false)} sx={{ position: "absolute", right: 15, top: 15 }}>
                <CloseIcon />
              </IconButton>
              <h2 id="otp-modal-title" style={{ textAlign: "center", marginBottom: "5px" }}>
                Verify {verificationType === "email" ? "Email" : "Phone"}
              </h2>
              <p style={{ textAlign: "center", marginBottom: "25px" }}>
                A verification OTP has been sent to your {verificationType}.
                Please check and enter it below.
              </p>
              <form className="form">
                <div className="control_group">
                  <div className="opt_fields">
                    <OTPInput
                      value={otp}
                      onChange={setOtp}
                      numInputs={4}
                      renderInput={(props) => <input {...props} id="otp" />}
                      inputType="tel"
                      shouldAutoFocus
                    />
                  </div>
                  <div className="auth_bottom">
                    <p style={{ textAlign: "center", margin: 0, display: "flex", justifyContent: "center" }}>
                      <button
                        type="button"
                        className="link anchor_link"
                        onClick={() => handleSendVerificationOtp(verificationType!)}
                        disabled={isSendingOtp || timer > 0}
                        style={{ pointerEvents: (isSendingOtp || timer > 0) ? "none" : "auto", opacity: (isSendingOtp || timer > 0) ? 0.6 : 1, border: "none", background: "transparent", fontSize: "14px", fontWeight: 600, color: "#D3B88C", cursor: "pointer" }}
                      >
                        {timer > 0 ? `Resend OTP (${timer}s)` : "Resend OTP"}
                      </button>
                    </p>
                  </div>
                </div>
                <div className="form_btn">
                  <Button
                    className="btn btn_primary w_100"
                    onClick={handleVerifyOtp}
                    disabled={isVerifyingOtp || timer === 0}
                  >
                    {isVerifyingOtp ? "Verifying..." : "Submit"}
                  </Button>
                </div>
              </form>
            </Box>
          </Modal>
        </div>
      </AuthLayout>
    </>
  );
};

export default SignupForm;
